import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMyLevels } from '../api/levels';
import { LEVEL_DEFS, MAX_LEVEL } from '../constants/levels';
import { useTranslation } from '../i18n';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { BottomNav } from '../components/ui/BottomNav';

export default function LevelLadderPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['levels'],
    queryFn:  () => getMyLevels().then((r) => r.data.data),
    staleTime: 60_000,
  });

  const currentLevel = data?.currentLevel ?? 0;
  const achievedAt = new Map((data?.progress ?? []).map((p) => [p.level, p.achievedAt]));
  const currentDef = LEVEL_DEFS.find((d) => d.level === currentLevel);

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>
      <header className="topbar" style={{
        position: 'sticky', top: 0, zIndex: 50, height: '64px', padding: '0 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--on-surface-3)', padding: '8px' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
          </button>
        </div>
      </header>

      <div className="page-body">
        <section style={{ marginBottom: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)' }}>
            <div style={{ fontFamily: 'var(--manrope)', fontWeight: 800, fontSize: '24px', color: 'var(--on-surface)', marginBottom: '4px' }}>
              {t('levels_page_title')}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--on-surface-3)' }}>
              {isLoading ? '…' : currentDef
                ? t('levels_page_subtitle').replace('{title}', t(currentDef.titleKey))
                : t('levels_page_subtitle_none')}
            </div>
          </div>
        </section>

        {!isLoading && (
          <div style={{ paddingBottom: '24px' }}>
            {LEVEL_DEFS.map((def) => {
              const isDone = achievedAt.has(def.level);
              const isCurrent = def.level === currentLevel;
              const isLast = def.level === MAX_LEVEL;

              const circleBg = isCurrent
                ? 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)'
                : isDone ? '#16a34a' : '#e5e7eb';
              const iconColor = isDone || isCurrent ? '#fff' : '#9ca3af';
              const statusLabel = isDone && !isCurrent
                ? t('level_status_done')
                : isCurrent ? t('level_status_current') : t('level_status_locked');
              const statusColor = isDone && !isCurrent ? '#16a34a' : isCurrent ? 'var(--primary)' : '#9ca3af';

              return (
                <div key={def.level} style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '9999px', background: circleBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: isCurrent ? '0 4px 12px rgba(0,91,192,0.3)' : 'none',
                    }}>
                      {isDone && !isCurrent ? (
                        <span className="material-symbols-outlined" style={{ fontSize: '22px', color: iconColor }}>check</span>
                      ) : (
                        <span className="material-symbols-outlined" style={{ fontSize: isCurrent ? '22px' : '20px', color: iconColor }}>
                          {def.icon}
                        </span>
                      )}
                    </div>
                    {!isLast && <div style={{ width: '2px', flex: 1, minHeight: '24px', background: isDone ? '#16a34a' : '#e5e7eb' }} />}
                  </div>
                  <div style={{ paddingBottom: isLast ? 0 : '24px', flex: 1, opacity: isDone || isCurrent ? 1 : 0.55 }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: statusColor, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                      {t('level_word')} {def.level} · {statusLabel}
                    </div>
                    <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '15px', color: 'var(--on-surface)', marginBottom: '4px' }}>
                      {t(def.titleKey)}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--on-surface-3)', lineHeight: 1.5 }}>
                      {t(def.descKey)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
